import isPlainObject from 'lodash/isPlainObject';

import { StatusIcon } from '$HVUI';

import './FormControl.module.less';

/**
 * Creates the CSS classnames for horizontal form columns.
 * @example
 * ```js
 * const labelCols = {
 *   xs: 5,
 *   sm: 12
 * };
 * const classNames = makeWidthClasses(labelCols)
 * // returns "ant-col ant-col-xs-5 ant-col-sm-12"
 * ```
 * @param {object} labelCol responsive column definition in the format specified by antd
 * @param {boolean} isForLabels Whether we are calculating the classes for the label area or the form control
 * @returns {string} Space separated CSS classes
 */
export const makeWidthClasses = (labelCol, isForLabels = true) => {
    if (!labelCol || !isPlainObject(labelCol)) return '';
    return ['ant-col']
        .concat(
            Object.keys(labelCol).map(size => {
                let span = labelCol[size] || 24;
                if (!Number.isInteger(span)) span = 24;
                const finalWidth =
                    isForLabels || span === 24 ? span : 24 - span;
                return `ant-col-${size}-${finalWidth}`;
            })
        )
        .join(' ');
};

const FormItem = props => {
    const {
        labelCol,
        layout,
        label,
        labelAlignment = 'left',
        id,
        children,
        type,
        maxWidth,
        requiredMark,
        shouldShowError,
        tooltip = {},
        style,
        className = ''
    } = props;

    const labelColClasses = makeWidthClasses(labelCol);
    const contentColClasses = makeWidthClasses(labelCol, false);

    const isFieldset = ['radio', 'multicheck'].includes(type);
    const WrappingEl = isFieldset ? 'fieldset' : 'div';
    const errorClass = shouldShowError ? 'ant-form-item-has-error' : null;
    const classNames = ['ant-row ant-form-item', className, errorClass]
        .filter(Boolean)
        .join(' ');
    return (
        <WrappingEl className={classNames} style={style}>
            {label && (
                <div
                    className={`ant-form-item-label ant-form-item-label-${labelAlignment} ${
                        layout == 'horizontal' ? labelColClasses : ''
                    }`}
                >
                    {isFieldset ? (
                        <legend>{label}</legend>
                    ) : (
                        <>
                            <label
                                className="ant-form-item-no-colon"
                                htmlFor={id}
                            >
                                {label} {requiredMark && <span>*</span>}
                            </label>
                            {tooltip?.status && (
                                <StatusIcon
                                    status={tooltip.status}
                                    style={{
                                        width: '16px',
                                        height: '16px'
                                    }}
                                    tooltipMaxWidth={tooltip?.maxWidth}
                                    tooltipPlacement={tooltip?.placement}
                                    tooltipText={tooltip?.text}
                                    useTooltip={true}
                                />
                            )}
                        </>
                    )}
                </div>
            )}
            <div
                className={`ant-form-item-control ${
                    layout == 'horizontal' ? contentColClasses : ''
                }`}
            >
                <div
                    className="ant-form-item-control-input-content"
                    style={{
                        maxWidth: maxWidth ? `${maxWidth}` : '100%'
                    }}
                >
                    {children}
                </div>
            </div>
        </WrappingEl>
    );
};

export default FormItem;
